import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Skeleton,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { usageService, projectService, personaService } from '../services/api';
import type { MonthlyUsageSummary, ProjectSummary, Persona } from '@personawiz/shared';

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recentProjects, setRecentProjects] = useState<ProjectSummary[]>([]);
  const [recentPersonas, setRecentPersonas] = useState<Persona[]>([]);
  const [usageSummary, setUsageSummary] = useState<MonthlyUsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Create Project Dialog state - opens if ?createProject=true
  const [createDialogOpen, setCreateDialogOpen] = useState(searchParams.get('createProject') === 'true');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const loadData = useCallback(async () => {
    const [usageResult, projectsResult, personasResult] = await Promise.allSettled([
      usageService.getSummary(),
      projectService.list({ pageSize: 3 }),
      personaService.list({ pageSize: 3 }),
    ]);

    if (usageResult.status === 'fulfilled') {
      setUsageSummary(usageResult.value);
    }
    if (projectsResult.status === 'fulfilled') {
      setRecentProjects(projectsResult.value?.data ?? []);
    }
    if (personasResult.status === 'fulfilled') {
      setRecentPersonas(personasResult.value?.data ?? []);
    }

    const allFailed =
      usageResult.status === 'rejected' &&
      projectsResult.status === 'rejected' &&
      personasResult.status === 'rejected';

    if (allFailed) {
      const reason = usageResult.reason as { code?: string; message?: string; response?: { status?: number } };
      if (reason?.code === 'ERR_NETWORK' || reason?.message?.includes('Network Error')) {
        setError('Unable to reach the server. Make sure the backend is running.');
      } else if (reason?.response?.status === 401) {
        // Handled centrally by the api.ts 401 interceptor (calls logout()).
        // No inline UI needed — the user will be redirected through the
        // standard sign-out → portal sign-in flow.
      } else {
        setError('Failed to load some dashboard data. Please try again later.');
      }
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadData();
    }, 0);

    return () => clearTimeout(timer);
  }, [loadData]);

  // Open dialog when navigating with ?createProject=true
  useEffect(() => {
    if (searchParams.get('createProject') === 'true') {
      const timer = setTimeout(() => {
        setCreateDialogOpen(true);
      }, 0);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [searchParams]);

  async function handleCreateProject() {
    if (!newProjectName.trim()) return;
    
    setIsCreating(true);
    try {
      const input: { name: string; description?: string } = {
        name: newProjectName.trim(),
      };
      if (newProjectDescription.trim()) {
        input.description = newProjectDescription.trim();
      }
      const newProject = await projectService.create(input);
      setCreateDialogOpen(false);
      setNewProjectName('');
      setNewProjectDescription('');
      // Navigate to the new project
      navigate(`/projects/${newProject.id}`);
    } catch (err) {
      console.error('Failed to create project:', err);
    } finally {
      setIsCreating(false);
    }
  }

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    // Remove the query param when closing
    if (searchParams.get('createProject')) {
      searchParams.delete('createProject');
      setSearchParams(searchParams);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Usage Summary */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Usage This Month
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {isLoading ? (
          [1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : usageSummary ? (
          <>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    API Calls
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {usageSummary.totalCalls.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total Tokens
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {(usageSummary.totalTokens / 1000).toFixed(1)}K
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Persona Gens
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {usageSummary.totalPersonaGenerations.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Est. Cost
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    ${usageSummary.totalCostUsd.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No usage data available yet. Generate your first persona to see stats here.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Welcome message when no projects and no personas */}
      {!isLoading && recentProjects.length === 0 && recentPersonas.length === 0 && (
        <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <LightbulbIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="body1" color="text.primary">
              Welcome to PersonaFab. Fabricate functional personas to optimize your AI interactions.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Two Columns: Recent Projects and Recent Personas */}
      <Grid container spacing={3}>
        {/* Most Recent Projects Column */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Most Recent Projects
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              New
            </Button>
          </Box>
          
          {isLoading ? (
            [1, 2, 3].map((i) => (
              <Card key={i} sx={{ mb: 2 }}>
                <CardContent>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            ))
          ) : recentProjects.length > 0 ? (
            recentProjects.map((project) => (
              <Card key={project.id} sx={{ mb: 2 }}>
                <CardActionArea onClick={() => navigate(`/projects/${project.id}`)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <FolderOpenIcon sx={{ mr: 1, color: 'info.main', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {project.name}
                      </Typography>
                    </Box>
                    {project.description && (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
                        {project.description}
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      icon={<PersonIcon />}
                      label={`${project.personaCount} persona${project.personaCount !== 1 ? 's' : ''}`}
                      variant="outlined"
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <FolderIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No projects yet
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  Create Project
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Most Recent Personas Column */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Most Recent Personas
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/personas/new')}
            >
              New
            </Button>
          </Box>
          
          {isLoading ? (
            [1, 2, 3].map((i) => (
              <Card key={i} sx={{ mb: 2 }}>
                <CardContent>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            ))
          ) : recentPersonas.length > 0 ? (
            recentPersonas.map((persona) => (
              <Card key={persona.id} sx={{ mb: 2 }}>
                <CardActionArea onClick={() => navigate(`/personas/${persona.id}`)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {persona.coreIdentity?.personaName || 'Unnamed Persona'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
                      {persona.coreIdentity?.userRole} — {persona.coreIdentity?.useCase}
                    </Typography>
                    {persona.tags && persona.tags.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {persona.tags.slice(0, 2).map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <PersonIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No personas yet
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/personas/new')}
                >
                  Create Persona
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Create Project Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            value={newProjectDescription}
            onChange={(e) => setNewProjectDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleCreateProject}
            disabled={!newProjectName.trim() || isCreating}
            sx={{ py: 1.5, fontWeight: 600 }}
          >
            {isCreating ? 'Creating...' : 'Create Project'}
          </Button>
          <Button onClick={handleCloseDialog} fullWidth>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
